import {
    Capabilities,
    Permissions,
    Roles,
    hasAnyCapability,
    hasAnyPermission,
    hasAnyRole,
    hasHealthAnalysisAccess,
    hasInsuranceNewsAccess,
} from '../../auth/rbac'

export const GA_LEADER_ROLES = [Roles.GA_LEADER, Roles.GA_MASTER_ADMIN, Roles.GA_MASTER_DEPUTY_ADMIN]
export const GA_MANAGER_ROLES = [Roles.GA_MANAGER, Roles.GA_BRANCH_MANAGER]
export const GA_OPERATION_MANAGER_ROLES = [Roles.GA_OPERATION_MANAGER, Roles.GA_AFFAIRS]
export const GA_PLANNER_ROLES = [Roles.GA_PLANNER]
export const INS_LEADER_ROLES = [Roles.INSURER_LEADER]
export const INS_ADMIN_ROLES = [Roles.INSURER_MANAGER, Roles.INSURER_BRANCH_MANAGER]
export const INS_MANAGER_ROLES = [Roles.INSURER_OPERATION_MANAGER, Roles.INSURER_EDUCATION_MANAGER]
export const INS_PLANNER_ROLES = [Roles.INSURER_PLANNER, Roles.INSURER_PLAN_MANAGER]

/**
 * Shell/App.js 기준 메뉴 노출 컨텍스트.
 * Phase 2에서 BE visibleMenuKeys와 병합할 때 동일 키 이름을 유지한다.
 */
export function buildMenuVisibilityContext(user, authHelpers = {}) {
    const hasRole = authHelpers.hasAnyRole || ((roles) => hasAnyRole(user, roles))
    const hasPermission = authHelpers.hasAnyPermission || ((perms) => hasAnyPermission(user, perms))
    const hasCapability = authHelpers.hasAnyCapability || ((caps) => hasAnyCapability(user, caps))

    const isOperator = hasRole([Roles.OPERATOR])
    const isGA =
        hasRole(GA_LEADER_ROLES) ||
        hasRole(GA_MANAGER_ROLES) ||
        hasRole(GA_OPERATION_MANAGER_ROLES) ||
        hasRole(GA_PLANNER_ROLES)
    const isInsurer =
        hasRole(INS_ADMIN_ROLES) ||
        hasRole(INS_LEADER_ROLES) ||
        hasRole(INS_MANAGER_ROLES) ||
        hasRole(INS_PLANNER_ROLES)
    const canUseRequests = hasCapability([Capabilities.REQUEST])
    const isPersonalOrgMember = String(user?.orgType || '').toUpperCase() === 'PERSONAL'

    const showOrgs =
        hasPermission([Permissions.ORG_MANAGE]) &&
        (!isPersonalOrgMember || isOperator)
    const showOrgBanners =
        hasPermission([Permissions.ORG_BANNER_MANAGE, Permissions.ORG_MANAGE]) &&
        (!isPersonalOrgMember || isOperator)
    const showOrgHomepageSales = !isPersonalOrgMember || isOperator
    const showOrgHomepage = showOrgHomepageSales
    const showUsers = isOperator || isGA || isInsurer
    const showRequests = (isOperator || isGA || isInsurer) && canUseRequests
    const showNotices = isOperator || isGA || isInsurer
    const showReports = hasPermission([Permissions.REPORT_VIEW])
    const hasOrgManagePermission = hasPermission([Permissions.ORG_MANAGE])
    const showOrgDashboard = Boolean(user?.orgDashboardAvailable || user?.org_dashboard_available) &&
        hasOrgManagePermission
    const showHealthAnalysis = Boolean(
        user && (isOperator || ((isGA || isInsurer) && hasHealthAnalysisAccess(user))),
    )
    const enableTermsLibrary =
        String(process.env.REACT_APP_ENABLE_TERMS_LIBRARY || '').toLowerCase() === 'true'
    const showTermsLibrary = enableTermsLibrary && isOperator
    const showFireBuildingRegistry = isOperator || isGA || isInsurer
    const showUw3nnExceptionsLookup = isOperator || isGA || isInsurer
    const showOperatorDesignTools = isOperator
    const showSupportInquiries = isOperator
    const showClaims = Boolean(user)
    const showInsuranceNews = Boolean(user && hasInsuranceNewsAccess(user))
    const showTemplateManage =
        isOperator || hasPermission([Permissions.TEMPLATE_MANAGE])

    return {
        isOperator,
        isGA,
        isInsurer,
        showDashboard: true,
        showOrgs,
        showOrgBanners,
        showOrgHomepageSales,
        showOrgHomepage,
        showUsers,
        showRequests,
        showNotices,
        showPersonalTaskBoard: Boolean(user),
        showSalesImages: true,
        showKakaoBulkSender: true,
        showBulkSms: false,
        showWorkSupportTools: isOperator || isGA || isInsurer,
        showReports,
        showOrgDashboard,
        showHealthAnalysis,
        showTermsLibrary,
        showFireBuildingRegistry,
        showUw3nnExceptionsLookup,
        showOperatorDesignTools,
        showSupportInquiries,
        showClaims,
        showSurveyManagement: true,
        showCustomerManagement: true,
        showCustomerSupportTools: true,
        showCustomerRegistration: isOperator,
        showInsuranceNews,
        showDataMgmtMenu: isOperator,
        showOpsMgmtMenu: isOperator,
        showSalesSupportMenu: true,
        showDesignSupportMenu:
            showHealthAnalysis ||
            showFireBuildingRegistry ||
            showUw3nnExceptionsLookup ||
            showRequests,
        showAdminMenu: showNotices || showOrgs || showUsers || showOrgDashboard || showOrgBanners,
        showTemplateManage,
        always: true,
        never: false,
    }
}

// showCustomerSupportMenu depends on other flags — compute after object creation
export function finalizeMenuVisibilityContext(ctx) {
    return {
        ...ctx,
        showCustomerSupportMenu:
            ctx.showCustomerManagement ||
            ctx.showCustomerSupportTools ||
            ctx.showSurveyManagement ||
            ctx.showSalesImages ||
            ctx.showKakaoBulkSender ||
            ctx.showClaims,
        showWorkSupportMenu:
            ctx.showPersonalTaskBoard ||
            ctx.isOperator ||
            ctx.isGA ||
            ctx.showWorkSupportTools,
    }
}

export function createMenuVisibilityContext(user, authHelpers = {}) {
    const base = finalizeMenuVisibilityContext(buildMenuVisibilityContext(user, authHelpers))
    const raw = user?.visibleMenuKeys ?? user?.visible_menu_keys
    const visibleMenuKeys = Array.isArray(raw) ? raw.filter(Boolean) : null
    const showOrgDashboard = visibleMenuKeys
        ? visibleMenuKeys.includes('MENU_ORG_DASHBOARD')
        : base.showOrgDashboard
    return {
        ...base,
        showOrgDashboard,
        showAdminMenu: base.showAdminMenu || showOrgDashboard,
        visibleMenuKeys,
    }
}

/**
 * @param {object} item menu manifest item
 * @param {object} ctx createMenuVisibilityContext 결과
 * @param {{ audience?: string }} [options]
 */
export function isMenuItemVisible(item, ctx, options = {}) {
    if (!item) return false
    const audience = options.audience || 'default'
    const key =
        (item.visibilityByAudience && item.visibilityByAudience[audience]) ||
        item.visibilityKey ||
        'never'
    if (key === 'never') return false
    if (key !== 'always' && !Boolean(ctx[key])) return false

    if (ctx.visibleMenuKeys && item.menuKey) {
        if (ctx.isOperator && item.operatorBypassVisibleMenuKeys) return true
        return ctx.visibleMenuKeys.includes(item.menuKey)
    }

    return key === 'always' || Boolean(ctx[key])
}

export function filterVisibleMenuItems(items, ctx, options = {}) {
    const { surface, audience } = options
    return (items || []).filter((item) => {
        if (surface && item.surfaces && !item.surfaces.includes(surface)) {
            return false
        }
        return isMenuItemVisible(item, ctx, { audience })
    })
}

/** Phase 2 BE visibleMenuKeys 연동용 */
export function getVisibleMenuKeys(items, ctx, options = {}) {
    return filterVisibleMenuItems(items, ctx, options)
        .map((item) => item.menuKey)
        .filter(Boolean)
}
